import * as chrono from "chrono-node";
import { toISODate } from "./dates.js";
import type { Collaborator, Label, Project, Section, Task } from "./entities.js";

/**
 * Todoist-style filter queries, e.g.
 *   "today | overdue"            "p1 & #Work"           "##Home & !subtask"
 *   "assigned to: me, @waiting"  "due before: friday"  "no date & !@someday"
 *
 * Operators: & (and), | (or), ! (not), ( ), and "," to split the result into separate lists.
 * Completed tasks never match.
 */

export class FilterError extends Error {}

export interface FilterContext {
  now?: Date;
  userId: string;
  projects: Pick<Project, "id" | "name" | "parentId" | "isInbox">[];
  sections?: Pick<Section, "id" | "name" | "projectId">[];
  labels: Pick<Label, "id" | "name">[];
  collaborators?: Pick<Collaborator, "id" | "name" | "email">[];
  /** projectId -> number of members, used by the "shared" term */
  memberCounts?: Record<string, number>;
  /** 0 = Sunday, 1 = Monday */
  startOfWeek?: number;
}

type Node =
  | { kind: "and" | "or"; left: Node; right: Node }
  | { kind: "not"; node: Node }
  | { kind: "term"; text: string };

type Token = { t: "(" | ")" | "&" | "|" | "!" } | { t: "term"; text: string };

function tokenize(input: string): Token[] {
  const out: Token[] = [];
  let buf = "";
  const flush = () => {
    const text = buf.trim();
    if (text) out.push({ t: "term", text });
    buf = "";
  };
  for (const ch of input) {
    if (ch === "(" || ch === ")" || ch === "&" || ch === "|") {
      flush();
      out.push({ t: ch });
    } else if (ch === "!" && buf.trim() === "") {
      buf = "";
      out.push({ t: "!" });
    } else {
      buf += ch;
    }
  }
  flush();
  return out;
}

function parseExpr(tokens: Token[]): Node {
  let pos = 0;
  const peek = () => tokens[pos];
  const or = (): Node => {
    let left = and();
    while (peek()?.t === "|") { pos++; left = { kind: "or", left, right: and() }; }
    return left;
  };
  const and = (): Node => {
    let left = unary();
    while (peek()?.t === "&") { pos++; left = { kind: "and", left, right: unary() }; }
    return left;
  };
  const unary = (): Node => {
    const tok = peek();
    if (!tok) throw new FilterError("Filter ends too early");
    if (tok.t === "!") { pos++; return { kind: "not", node: unary() }; }
    if (tok.t === "(") {
      pos++;
      const inner = or();
      if (peek()?.t !== ")") throw new FilterError("Missing closing bracket");
      pos++;
      return inner;
    }
    if (tok.t === "term") { pos++; return { kind: "term", text: tok.text }; }
    throw new FilterError(`Unexpected "${tok.t}"`);
  };
  const node = or();
  if (pos < tokens.length) throw new FilterError(`Unexpected "${(tokens[pos] as { t: string }).t}"`);
  return node;
}

/** Splits on commas that are not inside brackets. */
function splitGroups(query: string): string[] {
  const groups: string[] = [];
  let depth = 0;
  let buf = "";
  for (const ch of query) {
    if (ch === "(") depth++;
    if (ch === ")") depth--;
    if (ch === "," && depth === 0) { groups.push(buf); buf = ""; } else buf += ch;
  }
  groups.push(buf);
  return groups.map((g) => g.trim()).filter(Boolean);
}

const wildcard = (pattern: string) =>
  new RegExp(`^${pattern.trim().split("*").map((s) => s.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*")}$`, "i");

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return toISODate(d);
}

function parseDateTerm(text: string, now: Date): string | null {
  const lower = text.trim().toLowerCase();
  const today = toISODate(now);
  if (lower === "today") return today;
  if (lower === "tomorrow") return addDays(today, 1);
  if (lower === "yesterday") return addDays(today, -1);
  const r = chrono.en.GB.parse(text, now, { forwardDate: true })[0];
  if (!r || r.text.trim().length < text.trim().length - 1) return null;
  return toISODate(r.start.date());
}

type Pred = (t: Task) => boolean;

function compileTerm(raw: string, ctx: FilterContext, now: Date): Pred {
  const text = raw.trim();
  const t = text.toLowerCase();
  const today = toISODate(now);
  const due = (task: Task) => task.due?.date ?? null;

  if (t === "all" || t === "view all") return () => true;
  if (t === "today") return (x) => due(x) === today;
  if (t === "tomorrow") return (x) => due(x) === addDays(today, 1);
  if (t === "yesterday") return (x) => due(x) === addDays(today, -1);
  if (t === "overdue" || t === "od") {
    return (x) => !!x.due && (x.due.date < today || (!!x.due.datetime && x.due.date === today && new Date(x.due.datetime) < now));
  }
  if (t === "no date" || t === "no due date") return (x) => !x.due;
  if (t === "has date" || t === "has due date") return (x) => !!x.due;
  if (t === "no time") return (x) => !!x.due && !x.due.datetime;
  if (t === "has time") return (x) => !!x.due?.datetime;
  if (t === "recurring") return (x) => !!x.due?.isRecurring;
  if (t === "no deadline") return (x) => !x.deadline;
  if (t === "has deadline" || t === "deadline") return (x) => !!x.deadline;
  if (t === "no priority") return (x) => x.priority === 1;
  if (t === "no labels" || t === "no label") return (x) => x.labelIds.length === 0;
  if (t === "subtask" || t === "subtasks") return (x) => !!x.parentId;
  if (t === "assigned") return (x) => !!x.assigneeId;
  if (t === "shared") return (x) => (ctx.memberCounts?.[x.projectId] ?? 1) > 1;

  let m: RegExpMatchArray | null;
  if ((m = t.match(/^p([1-4])$/))) { const p = 5 - Number(m[1]); return (x) => x.priority === p; }
  if ((m = t.match(/^(?:next )?(\d+) days?$/))) {
    const end = addDays(today, Number(m[1]));
    return (x) => { const d = due(x); return !!d && d >= today && d < end; };
  }
  if (t === "this week" || t === "next week") {
    const sow = ctx.startOfWeek ?? 1;
    const offset = (now.getDay() - sow + 7) % 7;
    const start = addDays(today, -offset + (t === "next week" ? 7 : 0));
    const end = addDays(start, 7);
    return (x) => { const d = due(x); return !!d && d >= start && d < end; };
  }
  if ((m = text.match(/^(due|deadline)\s*(before|after|on)?\s*:\s*(.+)$/i))) {
    const field = m[1]!.toLowerCase();
    const op = (m[2] ?? "on").toLowerCase();
    const date = parseDateTerm(m[3]!, now);
    if (!date) throw new FilterError(`Can't understand the date in "${text}"`);
    const get = field === "due" ? due : (x: Task) => x.deadline;
    if (op === "before") return (x) => { const d = get(x); return !!d && d < date; };
    if (op === "after") return (x) => { const d = get(x); return !!d && d > date; };
    return (x) => get(x) === date;
  }
  if ((m = text.match(/^search\s*:\s*(.+)$/i))) {
    const needle = m[1]!.trim().toLowerCase();
    return (x) => x.content.toLowerCase().includes(needle) || x.description.toLowerCase().includes(needle);
  }
  if ((m = text.match(/^assigned to\s*:\s*(.+)$/i))) {
    const who = m[1]!.trim().toLowerCase();
    if (who === "me") return (x) => x.assigneeId === ctx.userId;
    if (who === "others") return (x) => !!x.assigneeId && x.assigneeId !== ctx.userId;
    const ids = matchPeople(who, ctx);
    return (x) => !!x.assigneeId && ids.has(x.assigneeId);
  }
  if ((m = text.match(/^assigned by\s*:\s*(.+)$/i))) {
    const who = m[1]!.trim().toLowerCase();
    const ids = who === "me" ? new Set([ctx.userId]) : matchPeople(who, ctx);
    return (x) => !!x.assigneeId && x.assigneeId !== x.createdBy && ids.has(x.createdBy);
  }
  if ((m = text.match(/^(?:added|created) by\s*:\s*(.+)$/i))) {
    const who = m[1]!.trim().toLowerCase();
    const ids = who === "me" ? new Set([ctx.userId]) : matchPeople(who, ctx);
    return (x) => ids.has(x.createdBy);
  }
  if (text.startsWith("##")) {
    const re = wildcard(text.slice(2));
    const roots = ctx.projects.filter((p) => re.test(p.isInbox ? "Inbox" : p.name)).map((p) => p.id);
    const ids = new Set(roots);
    let grew = true;
    while (grew) {
      grew = false;
      for (const p of ctx.projects) if (p.parentId && ids.has(p.parentId) && !ids.has(p.id)) { ids.add(p.id); grew = true; }
    }
    return (x) => ids.has(x.projectId);
  }
  if (text.startsWith("#")) {
    const re = wildcard(text.slice(1));
    const ids = new Set(ctx.projects.filter((p) => re.test(p.isInbox ? "Inbox" : p.name)).map((p) => p.id));
    return (x) => ids.has(x.projectId);
  }
  if (text.startsWith("/")) {
    const re = wildcard(text.slice(1) || "*");
    const ids = new Set((ctx.sections ?? []).filter((s) => re.test(s.name)).map((s) => s.id));
    return (x) => !!x.sectionId && ids.has(x.sectionId);
  }
  if (text.startsWith("@")) {
    const re = wildcard(text.slice(1));
    const ids = new Set(ctx.labels.filter((l) => re.test(l.name)).map((l) => l.id));
    return (x) => x.labelIds.some((l) => ids.has(l));
  }
  const date = parseDateTerm(text, now);
  if (date) return (x) => due(x) === date;
  throw new FilterError(`Unknown filter "${text}"`);
}

function matchPeople(who: string, ctx: FilterContext): Set<string> {
  return new Set((ctx.collaborators ?? [])
    .filter((c) => c.name.toLowerCase().includes(who) || c.email.toLowerCase().startsWith(who))
    .map((c) => c.id));
}

function compileNode(node: Node, ctx: FilterContext, now: Date): Pred {
  switch (node.kind) {
    case "term": return compileTerm(node.text, ctx, now);
    case "not": { const p = compileNode(node.node, ctx, now); return (x) => !p(x); }
    case "and": { const a = compileNode(node.left, ctx, now); const b = compileNode(node.right, ctx, now); return (x) => a(x) && b(x); }
    case "or": { const a = compileNode(node.left, ctx, now); const b = compileNode(node.right, ctx, now); return (x) => a(x) || b(x); }
  }
}

export interface CompiledGroup {
  name: string;
  test: (task: Task) => boolean;
}

/** Compiles a query into one predicate per comma-separated group. Throws FilterError. */
export function compileFilter(query: string, ctx: FilterContext): CompiledGroup[] {
  const now = ctx.now ?? new Date();
  const groups = splitGroups(query);
  if (!groups.length) throw new FilterError("Filter is empty");
  return groups.map((g) => {
    const pred = compileNode(parseExpr(tokenize(g)), ctx, now);
    return { name: g, test: (x: Task) => !x.completedAt && !x.deletedAt && pred(x) };
  });
}

export function runFilter(query: string, tasks: Task[], ctx: FilterContext): { name: string; tasks: Task[] }[] {
  return compileFilter(query, ctx).map((g) => ({ name: g.name, tasks: tasks.filter(g.test) }));
}

/** Returns an error message for an invalid query, or null when it is valid. */
export function validateFilter(query: string, ctx: FilterContext): string | null {
  try { compileFilter(query, ctx); return null; } catch (e) { return e instanceof Error ? e.message : String(e); }
}

/** Built-in smart lists shown on the Filters & Labels page. */
export const BUILTIN_FILTERS: { id: string; name: string; query: string; color: string }[] = [
  { id: "builtin-assigned", name: "Assigned to me", query: "assigned to: me", color: "blue" },
  { id: "builtin-p1", name: "Priority 1", query: "p1", color: "red" },
  { id: "builtin-week", name: "Next 7 days", query: "7 days | overdue", color: "green" },
  { id: "builtin-nodate", name: "No date", query: "no date & !subtask", color: "grey" },
  { id: "builtin-deadlines", name: "Deadlines", query: "has deadline", color: "orange" },
];
